<template>
  <b-modal id="processing-request-modal" hide-header hide-footer centered size="md">
    <div class="d-flex justify-content-center mt-5" v-if="loading">
      <div class="spinner-border" role="status"></div>
    </div>

    <div v-if="processingRequest" class="pr-modal">

      <div class="pr-modal__header">
        <i class="fas fa-hands-helping pr-modal__header-icon"></i>
        <span class="pr-modal__header-title">Processing Request</span>
        <button class="pr-modal__close" @click="$bvModal.hide('processing-request-modal')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="pr-modal__body">

        <!-- Confirmation screen after responding (this session) -->
        <div v-if="responded" class="pr-modal__confirmed">
          <div class="pr-modal__confirmed-icon">
            <i class="fas fa-paper-plane"></i>
          </div>
          <p class="pr-modal__confirmed-title">Response sent</p>
          <p class="pr-modal__confirmed-sub">We hope they find the support they need 🙏</p>
        </div>

        <!-- Already responded (previous session) -->
        <div v-else-if="alreadyResponded" class="pr-modal__confirmed">
          <div class="pr-modal__confirmed-icon" style="background:rgba(108,117,125,0.08);border-color:rgba(108,117,125,0.25);color:#6c757d;">
            <i class="fas fa-check-double"></i>
          </div>
          <p class="pr-modal__confirmed-title">Already responded</p>
          <p class="pr-modal__confirmed-sub">You have already responded to this request.</p>
          <router-link :to="'/chatbox/' + processingRequest.sender_id" class="prl-btn-gold mt-3">Open Messages</router-link>
        </div>

        <!-- Normal response UI -->
        <template v-else>
          <div class="pr-modal__sender">
            <img
              :src="processingRequest.sender.profile_image || 'https://media.iamconnected.com/profile_images/default.png'"
              class="pr-modal__avatar"
              alt=""
            />
            <div>
              <p class="pr-modal__name" v-text="processingRequest.sender.full_name"></p>
              <p v-if="senderLocation" class="pr-modal__location">
                <i class="fas fa-map-marker-alt mr-1"></i>{{ senderLocation }}
              </p>
            </div>
          </div>

          <div class="pr-modal__message">
            <p class="pr-modal__message-request"><strong>{{ processingRequest.sender.full_name }}</strong> has requested your support to hold space for a process.</p>
            <p v-text="processingRequest.message"></p>
          </div>

          <p class="pr-modal__respond-label">How do you want to respond?</p>

          <div class="pr-modal__actions">
            <button
                v-for="type in responseTypes"
                class="pr-modal__btn"
                :class="[type.type == responseType ? 'active' : undefined, 'pr-modal__btn--' + type.type]"
                :disabled="sending"
                @click="setResponseType(type.type)"
                :key="type.type"
            >
              <i :class="['fas', type.icon]"></i>
              <span>{{ type.label }}</span>
            </button>
          </div>

          <div class="w-100 mt-3">
            <textarea
                v-model="message"
                class="w-100 form-control"
                style="font-size: 14px"
                placeholder="Type a custom message instead..."
                rows="2"
            ></textarea>
          </div>

          <div class="mt-3 align-items-center d-flex justify-content-end mt-3 w-100" style="gap: 4px">
            <div v-if="sending" class="small text-muted">
              <i class="fas fa-spinner fa-spin mr-1"></i> Sending...
            </div>

            <b-button variant="link" class="text-muted" @click="$bvModal.hide('processing-request-modal')">
              Cancel
            </b-button>
            <button class="btn btn-primary" :disabled="!responseType || !message" @click="respond()">
              Submit
            </button>
          </div>

          <div class="text-right mt-3" v-if="errorMessage">
            <span class="text-danger text-right">{{ errorMessage }}</span>
          </div>
        </template>
      </div>

    </div>
  </b-modal>
</template>

<script>
import axios from '@/axios';

export default {
  name: 'ProcessingRequestModal',
  data() {
    return {
      processingRequest: null,
      responseType: 'responded',
      message: '',
      responseTypes: [
        {
          type: 'available',
          icon: 'fa-check-circle',
          label: 'Available now',
          message: "I'm available now.",
        },
        {
          type: 'later',
          icon: 'fa-clock',
          label: 'Later today',
          message: "I'm not available right now but can do later today.",
        },
        {
          type: 'schedule',
          icon: 'fa-calendar-alt',
          label: 'Schedule',
          message: "Let's schedule a time that works.",
        },
        {
          type: 'unavailable',
          icon: 'fa-times-circle',
          label: 'Unavailable',
          message: "I'm not available right now. I hope you can find someone to support you.",
        },
      ],
      loading: true,
      sending: false,
      responded: false,
      errorMessage: ''
    };
  },
  computed: {
    alreadyResponded() {
      return this.processingRequest && this.processingRequest.responded_at
    },
    senderLocation() {
      if (!this.processingRequest) return null;
      const parts = [this.processingRequest.sender.city, this.processingRequest.sender.country].filter(Boolean);
      return parts.join(', ');
    },
  },
  methods: {
    show(id) {
      this.sending = false;
      this.responded = false;
      this.loading = true
      this.fetchProcessingRequest(id)
      this.$nextTick(() => this.$bvModal.show('processing-request-modal'));
    },

    async fetchProcessingRequest(id) {
      this.loading = true
      this.processingRequest = await axios.get(`processingRequest/${id}`).then(x => x.data)
      this.loading = false
    },

    setResponseType(type) {
      if (this.responseType === type) {
        this.responseType = 'responded' //default; respond w/o any status
      } else {
        this.responseType = type;
      }

      this.message = this.responseTypes.find(x => x.type === this.responseType)?.message;
    },

    async respond() {
      this.sending = true;
      const { data } = await axios.post(`processingRequest/${this.processingRequest.id}/respond`, {
        type: this.responseType,
        message: this.message
      });
      this.sending = false;

      if (data.status > 0) {
        this.processingRequest.responded_at = new Date().toISOString();
        this.$bvModal.hide('processing-request-modal');
        return
      }

      this.errorMessage = Object.values(data.errors || {}).join('<br />')
          || 'An unknown error has occurred';
    },
  },
  mounted() {
    this.$root.$on('show-processing-request', this.show);
  },
  beforeDestroy() {
    this.$root.$off('show-processing-request', this.show);
  },
};
</script>

<style scoped>

textarea::placeholder {
  color: #999;
  font-size: 12px;
}

.pr-modal__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 16px 20px 12px;
  border-bottom: 1px solid rgba(214, 182, 116, 0.2);
}
.pr-modal__header-icon { color: #d6b674; font-size: 1.1rem; }
.pr-modal__header-title { flex: 1; font-weight: 700; font-size: 1rem; color: #333; }
.pr-modal__close {
  background: none; border: none; color: #aaa;
  font-size: 1rem; cursor: pointer; padding: 0; line-height: 1;
}
.pr-modal__close:hover { color: #555; }

.pr-modal__body { padding: 20px; }

.pr-modal__sender { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
.pr-modal__avatar {
  width: 52px; height: 52px; border-radius: 50%; object-fit: cover;
  border: 2px solid rgba(214, 182, 116, 0.35); flex-shrink: 0;
}
.pr-modal__name { font-weight: 700; font-size: 1rem; color: #222; margin: 0 0 3px; }
.pr-modal__location {
  font-size: 0.78rem; color: #aaa; margin: 0; display: flex; align-items: center;
}
.pr-modal__location i { font-size: 0.7rem; }

.pr-modal__message {
  background: #f7f7f7;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 22px;
  color: #333;
  font-size: 0.92rem;
  line-height: 1.6;
}
.pr-modal__message p { margin: 0; }
.pr-modal__message-request {
  font-size: 0.83rem;
  color: #999;
  margin: 0 0 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8e8e8;
  line-height: 1.5;
}

.pr-modal__respond-label {
  font-size: 0.72rem; font-weight: 700; color: #bbb;
  letter-spacing: 1px; text-transform: uppercase; margin-bottom: 12px;
}

.pr-modal__actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

.pr-modal__btn {
  display: flex; align-items: center; gap: 8px;
  padding: 11px 14px; border-radius: 10px;
  border: 1px solid #e8e8e8; background: #fafafa;
  cursor: pointer; font-size: 0.85rem; font-weight: 600; color: #333;
  transition: transform 0.13s, box-shadow 0.15s, border-color 0.15s, background 0.15s;
  text-align: left; width: 100%;

  i {
    font-size: 1rem; flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px); box-shadow: 0 4px 14px rgba(0,0,0,0.07);
  }

  &:disabled {
    opacity: 0.55; cursor: not-allowed;
  }
}

.pr-modal__btn--available {
  border-color: rgba(40,167,69,0.3);
  background: rgba(40,167,69,0.04);

  i {
    color: #28a745;
  }

  &.active {
    border-color: rgba(40,167,69,0.55);
    background: rgba(40,167,69,0.3);
  }
}

.pr-modal__btn--later {
  border-color: rgba(214,182,116,0.4);
  background: rgba(214,182,116,0.06);
  i {
    color: #d6b674;
  }
  &.active {
    border-color: rgba(214,182,116,0.7);
    background: rgba(214,182,116,0.3);
  }
}

.pr-modal__btn--schedule {
  border-color: rgba(0,123,255,0.25);
  background: rgba(0,123,255,0.04);

 i {
   color: #007bff;
 }

  &.active {
    border-color: rgba(0,123,255,0.5);
    background: rgba(0,123,255,0.3);
  }
}

.pr-modal__btn--unavailable {
  border-color: rgba(108,117,125,0.3);
  background: rgba(108,117,125,0.04);

  i {
    color: #6c757d;
  }
  &.active {
    border-color: rgba(108,117,125,0.5);
    background: rgba(108,117,125,0.3);
  }
}

/* Confirmation screen */
.pr-modal__confirmed {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 36px 20px;
  text-align: center;
  gap: 10px;
}
.pr-modal__confirmed-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(40,167,69,0.08);
  border: 1px solid rgba(40,167,69,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #28a745;
  font-size: 1.4rem;
  margin-bottom: 6px;
}
.pr-modal__confirmed-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin: 0;
}
.pr-modal__confirmed-sub {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
  line-height: 1.5;
}

.prl-btn-gold {
  display: inline-block;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 9px 22px;
  text-decoration: none;
  transition: opacity 0.18s, transform 0.18s;
  cursor: pointer;
}
</style>
